import * as types from "./actionTypes";
import * as hostApi from "../api/hostApi";
import { isNotFound } from "../api/client";
import { clearHostKey, getHostKey, normalizeHostKey, setHostKey } from "../session/hostKey";

export const hostSet = (host) => ({ type: types.HOST_SET, host });

const nobody = { hasKey: false, userId: null, canPublish: false };

/** Asks the server who the stored host key belongs to; a key it does not know is dropped. */
export const loadHostProfile = () => async (dispatch) => {
  if (!getHostKey()) {
    dispatch(hostSet(nobody));
    return null;
  }

  try {
    const me = await hostApi.getMe();
    dispatch(hostSet({ hasKey: true, userId: me.UserId, canPublish: me.CanPublish }));
    return me;
  } catch (error) {
    if (isNotFound(error)) {
      clearHostKey();
      dispatch(hostSet(nobody));
    } else {
      dispatch(hostSet({ hasKey: true }));
    }
    return null;
  }
};

/**
 * Replaces this device's host key with a fresh one; the old key stops working on every
 * device that had it. Resolves with the new key. Rejects when the server refuses (unknown
 * key, offline); the stored key is then unchanged.
 */
export const rotateHostKey = () => async (dispatch) => {
  const profile = await hostApi.rotateKey();
  const key = setHostKey(profile.HostKey);
  dispatch(hostSet({ hasKey: Boolean(key), userId: profile.UserId, canPublish: profile.CanPublish }));
  return key;
};

/**
 * Adopts a host key typed or linked on this device. Resolves to the code of the host's
 * current session, or null when the host has none yet. Rejects (and keeps the previous key)
 * when the server does not know the key.
 */
export const adoptHostKey = (value) => async (dispatch) => {
  const key = normalizeHostKey(value);
  if (!key) {
    const invalid = new Error("That is not a host key");
    invalid.code = "INVALID_HOST_KEY";
    throw invalid;
  }

  const previous = getHostKey();
  setHostKey(key);
  try {
    const me = await hostApi.getMe();
    dispatch(hostSet({ hasKey: true, userId: me.UserId, canPublish: me.CanPublish }));
  } catch (error) {
    if (previous) setHostKey(previous);
    else clearHostKey();
    throw error;
  }

  try {
    const session = await hostApi.getSession();
    return session.Code;
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};
